import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { resolveBrand } from "./brand";
import type { Brand } from "./brand";
import { SECTION_LABELS, sectionOf } from "./menuFor";
import type { Section, SectionId } from "./menuFor";

const ARIA = {
  nav: { hr: "Glavna navigacija", en: "Main navigation" },
  tabs: { hr: "Paneli sekcije", en: "Section panels" },
} as const;

function useIsDesktop(): boolean {
  const [desktop, setDesktop] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return true;
    }
    const m = window.matchMedia("(min-width: 768px)");
    return m.matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const m = window.matchMedia("(min-width: 768px)");
    const onChange = (event: MediaQueryListEvent) => setDesktop(event.matches);
    if (typeof m.addEventListener === "function") {
      m.addEventListener("change", onChange);
      return () => m.removeEventListener("change", onChange);
    }
    m.addListener(onChange);
    return () => m.removeListener(onChange);
  }, []);

  return desktop;
}

export interface ControlShellProps {
  brand: Brand;
  sections: Section[];
  labelFor: (id: string) => string;
  activePanel: string;
  onSelectPanel: (id: string) => void;
  lang: "hr" | "en";
  onToggleLang: () => void;
  themeControl: ReactNode;
  headerRight: ReactNode;
  children: ReactNode;
}

export function ControlShell({
  brand,
  sections,
  labelFor,
  activePanel,
  onSelectPanel,
  lang,
  onToggleLang,
  themeControl,
  headerRight,
  children,
}: ControlShellProps) {
  const resolvedBrand = resolveBrand(brand);
  const desktop = useIsDesktop();

  const activeSection: SectionId | undefined = sectionOf(activePanel, sections);
  const chatActive = activeSection === "chat";
  const active = sections.find(
    (section) => section.id === activeSection && section.panels.length > 0,
  );
  const activePanels = active ? active.panels : [];

  // Settings lives in the footer (bottom bar / sidebar) and never joins the
  // main navigation; it is rendered only when it carries at least one panel.
  const settings = sections.find(
    (section) => section.id === "postavke" && section.panels.length > 0,
  );
  const mainSections = sections.filter(
    (section) => section.id !== "postavke" && section.panels.length > 0,
  );

  const selectSection = (section: Section) => {
    if (section.id === activeSection) return;
    const first = section.panels[0];
    if (first === undefined) return;
    onSelectPanel(first);
  };

  const sectionButton = (section: Section) => {
    const isActive = section.id === activeSection;
    return (
      <button
        key={section.id}
        type="button"
        data-testid={`shell-nav-${section.id}`}
        aria-current={isActive ? "page" : undefined}
        className={`min-h-11 min-w-0 truncate rounded-md px-0 py-2 text-[10px] font-medium leading-tight tracking-tight transition-colors min-[380px]:px-0.5 min-[380px]:text-[11px] min-[380px]:tracking-normal md:flex-1 md:px-3 md:text-left md:text-sm md:tracking-normal ${
          isActive
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:bg-border hover:text-foreground"
        }`}
        onClick={() => selectSection(section)}
      >
        {SECTION_LABELS[section.id]?.[lang] ?? section.id}
      </button>
    );
  };

  const langToggle = (
    <button
      type="button"
      data-testid="control-lang-toggle"
      className="min-h-11 rounded-md px-1 py-2 text-xs font-medium text-muted-foreground hover:bg-border hover:text-foreground md:px-3 md:text-left md:text-sm"
      onClick={onToggleLang}
    >
      {lang === "hr" ? "HR / EN" : "EN / HR"}
    </button>
  );

  const settingsGear = settings ? (
    <button
      type="button"
      data-testid="shell-nav-postavke"
      aria-label={SECTION_LABELS.postavke[lang]}
      aria-current={settings.id === activeSection ? "page" : undefined}
      className={`flex min-h-11 min-w-11 items-center justify-center rounded-md ${
        settings.id === activeSection
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:bg-border hover:text-foreground"
      }`}
      onClick={() => selectSection(settings)}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
      >
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    </button>
  ) : null;

  return (
    <div
      className={
        chatActive
          ? "flex h-[100dvh] flex-col overflow-hidden bg-background text-foreground"
          : "min-h-screen bg-background text-foreground"
      }
    >
      <header className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-b border-border px-3 py-2 md:flex-nowrap md:gap-3 md:px-4 md:py-3 md:pl-60">
        {resolvedBrand.logo ? (
          <img
            src={resolvedBrand.logo}
            alt=""
            className="h-8 w-8 shrink-0 rounded-md object-contain"
          />
        ) : null}
        <h1 className="text-base font-semibold text-foreground md:text-lg">{resolvedBrand.name}</h1>
        {!desktop ? (
          <div className="ml-auto flex items-center gap-1">
            {settingsGear}
            {langToggle}
            <div className="flex items-center">{themeControl}</div>
          </div>
        ) : null}
        <div
          className={
            chatActive
              ? "hidden w-full min-w-0 items-center gap-2 md:flex md:ml-auto md:w-auto"
              : "flex w-full min-w-0 items-center gap-2 md:ml-auto md:w-auto"
          }
        >
          {headerRight}
        </div>
      </header>

      <nav
        aria-label={ARIA.nav[lang]}
        className="fixed inset-x-0 bottom-0 z-20 grid grid-flow-col auto-cols-fr items-stretch gap-0.5 border-t border-border bg-background px-1 pt-1 pb-[env(safe-area-inset-bottom)] md:flex md:inset-y-0 md:right-auto md:w-56 md:flex-col md:border-t-0 md:border-r md:px-2 md:pt-[4.75rem] md:pb-4"
      >
        {desktop ? (
          <>
            <div className="flex flex-1 md:flex-none md:flex-col md:gap-1">
              {mainSections.map((section) => sectionButton(section))}
            </div>

            <div
              data-testid="shell-footer"
              className="flex items-center md:mt-auto md:flex-col md:items-stretch md:gap-1 md:pt-4"
            >
              {settings ? sectionButton(settings) : null}
              {langToggle}
              <div className="flex items-center justify-center">{themeControl}</div>
            </div>
          </>
        ) : (
          <>{mainSections.map((section) => sectionButton(section))}</>
        )}
      </nav>

      <main
        className={
          chatActive
            ? "flex min-h-0 flex-1 flex-col overflow-hidden pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pl-60 md:pb-0"
            : "px-4 pt-3 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pl-60 md:pt-4 md:pb-8"
        }
      >
        {activePanels.length > 1 ? (
          <div
            role="tablist"
            aria-label={ARIA.tabs[lang]}
            className="mb-3 -mx-4 flex flex-nowrap gap-1 overflow-x-auto border-b border-border px-4 [scrollbar-width:none] md:mx-0 md:mb-4 md:px-0"
          >
            {activePanels.map((panelId) => {
              const selected = panelId === activePanel;
              return (
                <button
                  key={panelId}
                  type="button"
                  role="tab"
                  data-testid={`control-tab-${panelId}`}
                  aria-selected={selected}
                  className={`min-h-11 shrink-0 whitespace-nowrap rounded-t-md px-3 py-2 text-sm ${
                    selected
                      ? "border-b-2 border-primary font-medium text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => onSelectPanel(panelId)}
                >
                  {labelFor(panelId)}
                </button>
              );
            })}
          </div>
        ) : null}
        {children}
      </main>
    </div>
  );
}
